import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useChatStore } from '../../store/chatStore';
import type { ToolUseBlock, ToolResultBlock } from '../../store/chatStore';
import { SelectedToolDetails } from './SelectedToolDetails';
import { ToolPanelHeader } from './ToolPanelHeader';
import { ResizablePanel } from './ResizablePanel';
import type { ViewMode, PanelSize } from './types';
import { LoadingSpinner } from './LoadingSpinner';
import { getToolUseMeta } from '../../utils/toolUse';

interface ToolExecution {
  toolUse: ToolUseBlock;
  result?: ToolResultBlock;
}

const DEFAULT_INSPECT_TOOLS = new Set([
  'listAssets',
  'listFindings',
  'listRisks',
  'listVulnerabilities',
]);

const getDefaultViewMode = (toolName?: string): ViewMode =>
  toolName && DEFAULT_INSPECT_TOOLS.has(toolName) ? 'debug' : 'result';

export const ToolExecutionPanel: React.FC = () => {
  const [panelSize, setPanelSize] = useState<PanelSize>('expanded');
  const [modeState, setModeState] = useState<{
    executionId: string | null;
    mode: ViewMode;
  }>({
    executionId: null,
    mode: 'result',
  });

  const activeConversation = useChatStore((state) =>
    state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null,
  );
  const selectedToolId = useChatStore((state) => state.selectedToolId);
  const isLoading = activeConversation?.isLoading ?? false;
  const isOpen = activeConversation?.toolPanelOpen ?? false;

  const setOpen = useChatStore((state) => state.setToolPanelOpen);

  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );

  const toolExecution = useMemo<ToolExecution | null>(() => {
    if (!activeConversation) {
      return null;
    }

    const messages = activeConversation.messages;

    // If selectedToolId is provided, find that specific tool
    if (selectedToolId) {
      for (const message of messages) {
        for (const block of message.content) {
          if (block.type === 'toolUse' && block.id === selectedToolId) {
            const execution: ToolExecution = { toolUse: block };
            const result = message.content.find(
              (r): r is ToolResultBlock =>
                r.type === 'toolResult' && r.toolUseId === block.id,
            );
            if (result) {
              execution.result = result;
            }
            return execution;
          }
        }
      }
    }

    // Otherwise find the latest tool execution
    let latestExecution: ToolExecution | null = null;
    for (const message of messages) {
      for (const block of message.content) {
        if (block.type === 'toolUse') {
          const execution: ToolExecution = { toolUse: block };
          const result = message.content.find(
            (r): r is ToolResultBlock =>
              r.type === 'toolResult' && r.toolUseId === block.id,
          );
          if (result) {
            execution.result = result;
          }
          latestExecution = execution;
        }
      }
    }

    return latestExecution;
  }, [activeConversation, selectedToolId]);

  const executionId = toolExecution?.toolUse.id;
  const executionToolName = toolExecution?.toolUse.name;
  const defaultMode = getDefaultViewMode(executionToolName);
  const resolvedViewMode =
    executionId && modeState.executionId !== executionId
      ? defaultMode
      : modeState.mode;

  useEffect(() => {
    if (isOpen && executionId && modeState.executionId !== executionId) {
      // Track mode per execution while preserving the correct first-paint mode.
      setModeState({ executionId, mode: defaultMode });
    }
  }, [executionId, defaultMode, isOpen, modeState.executionId]);

  const handleClose = useCallback(() => {
    if (activeConversationId) {
      setOpen(activeConversationId, false);
    }
    setPanelSize('expanded');
  }, [activeConversationId, setOpen, setPanelSize]);

  if (!toolExecution || !isOpen) {
    return null;
  }

  const toolUseMeta = getToolUseMeta(toolExecution.toolUse);
  const toolSource = (toolExecution.toolUse.meta as { source?: string })
    ?.source;

  return (
    <ResizablePanel panelSize={panelSize}>
      <ToolPanelHeader
        toolCount={1}
        viewMode={resolvedViewMode}
        panelSize={panelSize}
        onViewModeChange={(mode) =>
          setModeState({ executionId: executionId ?? null, mode })
        }
        onPanelSizeChange={setPanelSize}
        onClose={handleClose}
        toolName={toolUseMeta?.humanReadableName || toolExecution.toolUse.name}
        toolSource={toolSource}
      />
      {isLoading && !toolExecution.result && resolvedViewMode !== 'debug' ? (
        <LoadingSpinner />
      ) : (
        <SelectedToolDetails
          selectedToolId={toolExecution.toolUse.id}
          viewMode={resolvedViewMode}
          panelSize={panelSize}
        />
      )}
    </ResizablePanel>
  );
};
